"use client";

import { motion, useReducedMotion } from "motion/react";
import type { CSSProperties, ReactNode } from "react";

/** The reference's reveal ease (quad-out as easings.net writes it; the
    same curve as custom.css --oi-ease-reveal). Nothing bounces. */
export const EASE: [number, number, number, number] = [0.5, 1, 0.89, 1];

interface RevealProps {
  children: ReactNode;
  /** Seconds. Stagger siblings in 0.15 steps (the spec's line stagger). */
  delay?: number;
  duration?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Scroll-triggered entrance: opacity + blur only, no translate (the
 * reference never moves elements on scroll). Fires once at "top 90%",
 * gated on prefers-reduced-motion. The single reveal primitive.
 */
export function Reveal({
  children,
  delay = 0,
  duration = 1.0,
  className,
  style,
}: RevealProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}
